import {
	Box,
	Divider,
	MenuItem,
	MenuList,
	Popover,
	Typography,
	ListItemButton,
	ListItemIcon,
	SvgIcon,
	ListItemText,
	Avatar,
	Stack,
	Chip,
	Alert,
} from "@mui/material";
import SwitchAccessShortcutIcon from '@mui/icons-material/SwitchAccessShortcut';
import LogoutIcon from '@mui/icons-material/Logout';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StoreIcon from '@mui/icons-material/Store';
import PersonIcon from '@mui/icons-material/Person';
import { useAuthContext, isRootAdmin } from "../contexts/auth-context";
import { useState } from "react";
import { ShopSelector } from "./login/shop.selector";
import { useShopDetails } from "../hooks/use-shop";
import ImpersonateDialog from "./impersonation/impersonate.dialog";

interface AccountPopoverProps {
	anchorEl: HTMLElement | null;
	onClose: () => void;
	open: boolean;
	onShopChange?: (error?: string) => void;
	onShopChangeComplete?: () => void;
}

export const AccountPopover = (props: AccountPopoverProps) => {
	const { anchorEl, onClose, open, onShopChange, onShopChangeComplete } = props;
	const { jwtDecoded, signOut } = useAuthContext();
	const { shopName, locationName } = useShopDetails();
	const [openShopSelector, setOpenShopSelector] = useState(false);
	const [openImpersonateDialog, setOpenImpersonateDialog] = useState(false);
	
	// Check if user is a super admin
	const isSuperAdmin = isRootAdmin(jwtDecoded);

	const initials = jwtDecoded?.email
		? `${jwtDecoded.email.charAt(0)}${jwtDecoded.email.charAt(1)}`.toUpperCase()
		: 'NA';

	const handleSwitchShop = () => {
		setOpenShopSelector(true);
		onShopChange?.();
		onClose();
	};

	const handleShopSelectorClose = () => {
		setOpenShopSelector(false);
		onShopChangeComplete?.();
	};

	const handleShopSelectorError = (error: string) => {
		onShopChange?.(error);
	};

	const handleSignOut = () => {
		onClose();
		signOut();
	};

	const handleImpersonateUser = () => {
		setOpenImpersonateDialog(true);
		onClose();
	};

	return (
		<>
			<Popover
				anchorEl={anchorEl}
				anchorOrigin={{
					horizontal: "right",
					vertical: "bottom",
				}}
				transformOrigin={{
					horizontal: "right",
					vertical: "top",
				}}
				onClose={onClose}
				open={open}
				PaperProps={{ 
					sx: { 
						width: 280,
						p: 2,
						mt: 1,
						boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
					}
				}}
			>
				<Stack spacing={2}>
					<Stack direction="row" spacing={2} alignItems="center">
						<Avatar
							sx={{
								height: 40,
								width: 40,
								backgroundColor: 'primary.main',
							}}
						>
							{initials}
						</Avatar>
						<Box sx={{ flexGrow: 1 }}>
							<Typography variant="subtitle1">
								{jwtDecoded?.email?.split('@')[0]}
							</Typography>
							<Typography color="text.secondary" variant="body2">
								{jwtDecoded?.email}
							</Typography>
						</Box>
					</Stack>

					{shopName ? (
						<Stack spacing={1}>
							<Stack direction="row" spacing={1} alignItems="center">
								<StoreIcon color="action" fontSize="small" />
								<Typography variant="body2" color="text.secondary">
									Current Shop
								</Typography>
							</Stack>
							<Typography variant="subtitle2">
								{shopName}
							</Typography>
							{locationName && (
								<Chip
									icon={<LocationOnIcon />}
									label={locationName}
									size="small"
									variant="outlined"
									sx={{ alignSelf: 'flex-start' }}
								/>
							)}
						</Stack>
					) : (
						<Alert 
							severity="warning"
							sx={{ 
								'& .MuiAlert-message': { 
									width: '100%' 
								}
							}}
						>
							<Stack spacing={1}>
								<Typography variant="body2">
									No shop selected
								</Typography>
								<ListItemButton
									onClick={handleSwitchShop}
									sx={{
										borderRadius: 1,
										px: 1,
										py: 0.5,
										backgroundColor: 'background.paper',
									}}
								>
									<ListItemIcon sx={{ minWidth: 36 }}>
										<SvgIcon fontSize="small">
											<StoreIcon />
										</SvgIcon>
									</ListItemIcon>
									<ListItemText
										primary={<Typography variant="body2">Select Shop</Typography>}
									/>
								</ListItemButton>
							</Stack>
						</Alert>
					)}

					<Divider />

					{shopName && (
						<ListItemButton
							onClick={handleSwitchShop}
							sx={{
								borderRadius: 1,
								px: 1,
								py: 0.5,
							}}
						>
							<ListItemIcon>
								<SvgIcon fontSize="small">
									<SwitchAccessShortcutIcon />
								</SvgIcon>
							</ListItemIcon>
							<ListItemText
								primary={<Typography variant="body1">Switch Shop</Typography>}
							/>
						</ListItemButton>
					)}

					{/* Impersonate User option for super admins */}
					{isSuperAdmin && !jwtDecoded?.isImpersonating && (
						<ListItemButton
							onClick={handleImpersonateUser}
							sx={{
								borderRadius: 1,
								px: 1,
								py: 0.5,
							}}
						>
							<ListItemIcon>
								<SvgIcon fontSize="small">
									<PersonIcon />
								</SvgIcon>
							</ListItemIcon>
							<ListItemText
								primary={<Typography variant="body1">Impersonate User</Typography>}
							/>
						</ListItemButton>
					)}

					<ListItemButton
						onClick={handleSignOut}
						sx={{
							borderRadius: 1,
							px: 1,
							py: 0.5,
						}}
					>
						<ListItemIcon>
							<SvgIcon fontSize="small">
								<LogoutIcon />
							</SvgIcon>
						</ListItemIcon>
						<ListItemText
							primary={<Typography variant="body1">Sign Out</Typography>}
						/>
					</ListItemButton>
				</Stack>
			</Popover>

			<ShopSelector
				open={openShopSelector}
				onClose={handleShopSelectorClose}
				onError={handleShopSelectorError}
			/>

			{/* Impersonation Dialog */}
			<ImpersonateDialog
				open={openImpersonateDialog}
				onClose={() => setOpenImpersonateDialog(false)}
			/>
		</>
	);
};
